import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import SalesReportForm from './SalesReportForm';
import './SalesReportsDashboard.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function SalesReportsDashboard() {
  const { user, hasPermission } = useAuth();
  const [reports, setReports] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [filters, setFilters] = useState({
    employee: 'all',
    status: 'all',
    startDate: '',
    endDate: ''
  });

  // Check permissions
  const canViewAll = hasPermission('sales-reports', 'read');
  const canCreate = hasPermission('sales-reports', 'create');
  const canReview = hasPermission('sales-reports', 'update');

  useEffect(() => {
    fetchReports();
    if (canViewAll) {
      fetchAnalytics();
      fetchEmployees();
    }
  }, [filters]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key] !== 'all') {
          params.append(key, filters[key]);
        }
      });

      const response = await api.get(`/sales-reports?${params.toString()}`);
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching sales reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.employee !== 'all') params.append('employee', filters.employee);

      const response = await api.get(`/sales-reports/analytics/dashboard?${params.toString()}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      const salesEmployees = response.data.filter(emp =>
        emp.role?.name === 'Sales' || emp.role?.permissions?.some(p => p.module === 'leads')
      );
      setEmployees(salesEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleCreateReport = () => {
    setEditingReport(null);
    setShowForm(true);
  };

  const handleEditReport = (report) => {
    setEditingReport(report);
    setShowForm(true);
  };

  const handleSaveReport = (savedReport) => {
    if (editingReport) {
      setReports(prev => prev.map(r => r._id === savedReport._id ? savedReport : r));
    } else {
      setReports(prev => [savedReport, ...prev]);
    }
    setShowForm(false);
    setEditingReport(null);
    if (canViewAll) fetchAnalytics();
  };

  const getChartData = () => {
    // Group reports by day of week
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const data = {
      calls: new Array(7).fill(0),
      connections: new Array(7).fill(0),
      qualified: new Array(7).fill(0)
    };

    reports.forEach(report => {
      const date = new Date(report.reportDate);
      const dayIndex = date.getDay() === 0 ? 6 : date.getDay() - 1; // 0=Sun -> 6, 1=Mon -> 0
      if (dayIndex >= 0 && dayIndex < 7) {
        data.calls[dayIndex] += report.totalOutboundCalls || 0;
        data.connections[dayIndex] += report.successfulConnections || 0;
        data.qualified[dayIndex] += report.qualifiedProspects || 0;
      }
    });

    return {
      labels: days,
      datasets: [
        {
          label: 'Calls',
          data: data.calls,
          backgroundColor: '#3b82f6',
          borderRadius: 4,
          barThickness: 12,
        },
        {
          label: 'Connections',
          data: data.connections,
          backgroundColor: '#22c55e',
          borderRadius: 4,
          barThickness: 12,
        },
        {
          label: 'Qualified',
          data: data.qualified,
          backgroundColor: '#a855f7',
          borderRadius: 4,
          barThickness: 12,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: { usePointStyle: true, boxWidth: 8 }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { borderDash: [2, 4], drawBorder: false },
        ticks: { stepSize: 20 }
      },
      x: {
        grid: { display: false }
      }
    }
  };



  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

  return (
    <div className="sales-reports-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-left">
          <h2>Daily Work Reports - Sales Team</h2>
          <p>Insights for {currentMonth}</p>
        </div>
        <div className="header-right">
          {canCreate && (
            <button className="btn-create-report" onClick={handleCreateReport}>
              <span>+</span> Create Report
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {canViewAll && analytics && (
        <div className="stats-grid">
          {/* Card 1: Outbound Calls (Blue) */}
          <div className="stat-card-new stat-card-blue">
            <div>
              <div className="stat-header">
                <span className="stat-icon">📞</span>
                <span className="stat-title">Total Outbound Calls</span>
              </div>
              <div className="stat-value-row">
                <span className="stat-value">{analytics.totalCalls || 0}</span>
                <span className="stat-trend trend-up">↑ 14%</span>
              </div>
            </div>
            <div className="progress-container">
              <div className="progress-bar-bg">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${analytics.connectionRate || 0}%`, background: '#0369a1' }}
                ></div>
              </div>
              <div className="stat-footer">Connection Rate: {analytics.connectionRate || 0}%</div>
            </div>
          </div>

          {/* Card 2: Connections (Green) */}
          <div className="stat-card-new stat-card-green">
            <div>
              <div className="stat-header">
                <span className="stat-icon">✅</span>
                <span className="stat-title">Successful Call Connections</span>
              </div>
              <div className="stat-value-row">
                <span className="stat-value">{analytics.totalConnections || 0}</span>
                <span className="stat-trend trend-up">↑ 8%</span>
              </div>
            </div>
            <div className="progress-container">
              <div className="progress-bar-bg">
                <div
                  className="progress-bar-fill"
                  style={{ width: '70%', background: '#15803d' }}
                ></div>
              </div>
              <div className="stat-footer">All Stages</div>
            </div>
          </div>

          {/* Card 3: Follow-ups (Yellow) */}
          <div className="stat-card-new stat-card-yellow">
            <div>
              <div className="stat-header">
                <span className="stat-icon">📅</span>
                <span className="stat-title">Follow-up Interactions Completed</span>
              </div>
              <div className="stat-value-row">
                <span className="stat-value">{analytics.totalFollowUps || 0}</span>
                <span className="stat-trend trend-down">↓ 0%</span>
              </div>
            </div>
            <div className="progress-container">
              <div className="progress-bar-bg">
                <div
                  className="progress-bar-fill"
                  style={{ width: '40%', background: '#a16207' }}
                ></div>
              </div>
              <div className="stat-footer">
                Avg duration: {analytics.totalReports ? Math.round(analytics.totalCallDuration / analytics.totalReports) : 0} min
              </div>
            </div>
          </div>

          {/* Card 4: Qualified (Purple) */}
          <div className="stat-card-new stat-card-purple">
            <div>
              <div className="stat-header">
                <span className="stat-icon">🎯</span>
                <span className="stat-title">Qualified Interested Prospects</span>
              </div>
              <div className="stat-value-row">
                <span className="stat-value">{analytics.totalQualifiedProspects || 0}</span>
                <span className="stat-trend trend-up">↑ 10%</span>
              </div>
            </div>
            <div className="progress-container">
              <div className="progress-bar-bg">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${analytics.conversionRate || 0}%`, background: '#7e22ce' }}
                ></div>
              </div>
              <div className="stat-footer">Conversion Rate: {analytics.conversionRate || 0}%</div>
            </div>
          </div>
        </div>
      )}

      {/* Reports List */}
      <h3 className="section-title">All Reports</h3>
      <div className="reports-list-container">
        {loading ? (
          <div className="loading">Loading reports...</div>
        ) : reports.length === 0 ? (
          <div className="no-data"><p>No reports found.</p></div>
        ) : (
          reports.map(report => (
            <div key={report._id} className="report-row" onClick={() => handleEditReport(report)}>
              <div className="user-info">
                <div className="user-avatar">
                  {report.employee.firstName.charAt(0)}{report.employee.lastName.charAt(0)}
                </div>
                <div className="user-details">
                  <h4>{report.employee.firstName} {report.employee.lastName}</h4>
                  <p>{new Date(report.reportDate).toLocaleDateString()}</p>
                </div>
              </div>

              <div className="report-metrics-row">
                <div className="metric-col">
                  <span className="metric-status status-submitted">
                    {report.status}
                  </span>
                </div>

                <div className="metric-col">
                  <span>Connections</span>
                  <strong>{report.successfulConnections}</strong>
                </div>

                <div className="metric-col">
                  <span>Qualified</span>
                  <strong>{report.qualifiedProspects}</strong>
                </div>

                <div className="metric-col">
                  <span>Pipeline</span>
                  <strong>{report.pipelineOpportunities}</strong>
                </div>

                <div className="metric-col" style={{ width: '60px' }}>
                  <div className="progress-bar-bg" style={{ height: '4px', margin: '8px 0' }}>
                    <div
                      className="progress-bar-fill"
                      style={{
                        width: `${Math.min((report.successfulConnections / (report.totalOutboundCalls || 1)) * 100, 100)}%`,
                        background: '#3b82f6'
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="row-action">›</div>
            </div>
          ))
        )}
      </div>

      {/* Weekly Performance Chart */}
      {canViewAll && (
        <div className="chart-section">
          <h3 className="section-title">Weekly Team Performance</h3>
          <div className="chart-container">
            <Bar data={getChartData()} options={chartOptions} />
          </div>
        </div>
      )}

      {/* Report Form Modal */}
      {showForm && (
        <SalesReportForm
          report={editingReport}
          onSave={handleSaveReport}
          onCancel={() => {
            setShowForm(false);
            setEditingReport(null);
          }}
        />
      )}
    </div>
  );
}

export default SalesReportsDashboard;